import { Component, Input, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { CertificationChartComponent } from './certification-chart/certification-chart.component';
import { CertificationTableComponent } from './certification-table/certification-table.component';
import { QaiLoadingComponent } from '@qai/ui';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ICertificationAssignmentStatsRequest } from 'src/app/interfaces/reportingV2/icertification-assignment-stats-request';
import { ICertificationAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/icertification-assignment-stats-response';
import { AssignmentCategory } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-request';
import { AssignmentFilterComponent, DEFAULT_STATUS_FILTER } from '../assignment-filter/assignment-filter.component';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { filterAssignmentsByStatus } from 'src/app/utils/reporting-dashboard.util';

@Component({
  selector: 'app-certification-tab',
  templateUrl: './certification-tab.component.html',
  styleUrls: ['./certification-tab.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    CommonModule,
    TranslocoModule,
    CertificationChartComponent,
    CertificationTableComponent,
    QaiLoadingComponent,
    AssignmentFilterComponent,
  ],
})
export class CertificationTabComponent implements OnInit, OnChanges {
  @Input() selectedAccount: { label: string; value: string } | null = null;
  @Input() selectedTeams: string[] = [];
  @Input() tabActive: boolean = false;
  isTranslationLoaded: boolean = false;
  isLoading: boolean = false;
  isNetworkCallInprogress: boolean = false;
  useStatusFilter: boolean = false;
  certificationStats: ICertificationAssignmentStatsResponse[] = [];
  visibleCertificationStats: ICertificationAssignmentStatsResponse[] = [];
  currentFilter: AssignmentCategory = 'RECENT_ASSIGNMENTS';

  // Status filter
  allCertificationStats: ICertificationAssignmentStatsResponse[] = [];
  selectedStatuses: string[] = [];
  syncedStatuses: string[] | null = null;
  private statsLoaded: boolean = false;

  constructor(
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isTranslationLoaded && (changes['selectedAccount'] || changes['selectedTeams'])) {
      this.statsLoaded = false;
      this.loadCertificationStats();
    }
    if (changes['tabActive'] && this.tabActive && !changes['tabActive'].firstChange) {
      const stored = ReportingLocalStorageUtil.getStoredRollupStatusFilter() ?? [...DEFAULT_STATUS_FILTER];
      this.selectedStatuses = stored;
      this.syncedStatuses = [...stored];
      if (this.statsLoaded) {
        this.applyStatusFilter();
      }
    }
  }

  async ngOnInit() {
    this.useStatusFilter = await this.featureFlagsService.isFlagEnabled(
      AppConstants.REPORTING_ASSIGNMENT_STATUS_FILTER
    );
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.isLoading = false;
    });
  }

  onVisibleStatsChanged(visibleStats: ICertificationAssignmentStatsResponse[]): void {
    this.visibleCertificationStats = visibleStats;
  }

  onAssignmentChange(event: any) {
    this.currentFilter = event as AssignmentCategory;
    this.loadCertificationStats();
  }

  onStatusFilterChange(statuses: string[]): void {
    this.selectedStatuses = statuses;
    if (!this.statsLoaded) {
      this.loadCertificationStats();
    } else {
      this.applyStatusFilter();
    }
  }

  private applyStatusFilter(): void {
    this.certificationStats = filterAssignmentsByStatus(this.allCertificationStats, this.selectedStatuses);
    this.visibleCertificationStats = [];
  }

  loadCertificationStats(): void {
    if (!this.selectedAccount?.value) {
      console.log('No account selected, skipping certification stats load');
      return;
    }

    this.isNetworkCallInprogress = true;

    // Handle "all" selection - if selectedTeams contains "all", pass empty array
    const teamUuids = this.selectedTeams.includes('all')
      ? []
      : this.selectedTeams;

    const request: ICertificationAssignmentStatsRequest = {
      accountUuid: this.selectedAccount.value,
      teamUuids: teamUuids,
      category: this.useStatusFilter ? 'ALL_ASSIGNMENTS' : this.currentFilter,
    };

    this.reportingDashboardService
      .getCertificationAssignmentStats(request, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .subscribe({
        next: (response) => {
          this.isNetworkCallInprogress = false;
          this.statsLoaded = true;
          if (this.useStatusFilter) {
            this.allCertificationStats = response;
            this.applyStatusFilter();
          } else {
            this.certificationStats = response;
          }
          console.log('Certification stats loaded:', response);
        },
        error: (error) => {
          console.error('Error loading certification stats:', error);
          this.isNetworkCallInprogress = false;
          this.statsLoaded = true;
          this.certificationStats = [];
          this.allCertificationStats = [];
          this.visibleCertificationStats = [];
        },
      });
  }
}
